'use client';

import { useMemo } from 'react';
import { useFragment, useQuery, useSuspenseQuery } from '@apollo/client';
import RecommendationItem from '../../components/by-type/recommendations/recommendation-item';
import NoComments from 'shared/misc/components/by-type/feed/utils/NoComments';
import {
  GetRecViewDocument,
  GetRecViewQuery,
  ProfileThemeType,
  RecType,
  RecommendationItemFragment,
  RecommendationItemFragmentDoc,
  RelatedRecsQuery,
} from 'shared/__generated__/graphql';
import CommentsContainer from 'web2/app/components/by-type/comments/comments-container';
import { RecommendationContext } from 'shared/misc/components/by-type/recommendations/providers/useRecommendationContext';
import RecViewRelatedRecs from 'web2/app/components/by-type/recommendations/rec-view-related-recs';
import { twMerge } from 'tailwind-merge';
import { Suspense } from 'react';
import { TWLink } from '../../components/link';
import CommentComposer from 'web2/app/components/by-type/comments/comment-composer';
import { useAuth } from 'shared/misc/hooks/useAuth';
import SkeletonCommentItem from 'shared/misc/components/SkeletonCommentItem';
import { useAutoSetTheme } from 'shared/misc/providers/ThemeContext';
import SkeletonFeedItem from 'shared/misc/components/SkeletonFeedItem';

type RecViewProps = {
  id: string;
  initialRecData?: GetRecViewQuery['rec'];
  initialRelatedRecsData?: RelatedRecsQuery;
};

export default function RecView({ id, initialRecData, initialRelatedRecsData }: RecViewProps) {
  const recViewQuery = useQuery(GetRecViewDocument, {
    variables: { id },
    initialFetchPolicy: 'cache-first',
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    pollInterval: 120000,
    notifyOnNetworkStatusChange: true,
    staleTime: 60000,
  });

  const auth = useAuth();

  const rec = (recViewQuery.data?.rec?.id ? recViewQuery.data?.rec : initialRecData || null)!;

  const ctx = useMemo(() => ({ rec }), [rec?.id, rec?.updatedAt]) as {
    rec: RecommendationItemFragment;
  };

  const realRec = rec.type === RecType.RePost && rec.repostTargetRec ? rec.repostTargetRec : rec;

  useAutoSetTheme(
    rec.user?.profileTheme || auth.user?.profileTheme || ProfileThemeType.Default,
    rec.user?.customTheme?.colors || auth.user?.customTheme?.colors || null,
  );

  // if (!loading && fragmentData?.id == null && data?.rec?.id == null) {
  //   // return redirect('/not-found');
  //   return <div>Not found</div>;
  // }

  // if (!rec?.id) {
  //   return null;
  // }

  if (rec.type === RecType.RePost && rec.repostTargetRec) {
    // return redirect(`/rec/${rec.repostTargetRec.id}`);
  }

  const commentsCount = realRec.commentsCount || 0;

  const isPrivateShare =
    realRec &&
    (realRec.type === RecType.Private ||
      realRec.type === RecType.PrivateShareExisting ||
      realRec.type === RecType.PrivateShareNew);

  return (
    <RecommendationContext.Provider value={ctx}>
      <div
        className={
          'w-full md:w-[calc(100vw-128px)] 2xl:w-[1400px] flex flex-col md:flex-row ' +
          'mx-auto gap-6 justify-center'
        }
      >
        <div className={'flex flex-col flex-1 bg-brand-background'}>
          <div
            className={
              'border-b border-dashed md:border-none border-brand-main md:border-transparent ' +
              'pb-6 md:pb-0'
            }
          >
            <RecommendationItem
              rec={rec}
              withBorder={false}
              showPrompt
              withResponses={false}
              isRecView
              noOfLines={200}
              enableEmbed
            />
          </div>
          <div
            className={twMerge(
              'flex flex-col gap-[3px] w-full self-center',
              `${realRec.commentsCount ? 'mb-10' : 'mb-0'} px-6 mt-[16px]`,
            )}
          >
            <p className={'text-sm md:text-base text-brand-main font-brand-heading'}>Comments ({commentsCount})</p>
            {!isPrivateShare ? (
              <>
                {auth.user ? (
                  <div className="mb-6 mt-6">
                    <CommentComposer targetRecId={realRec?.id || id} />
                  </div>
                ) : (
                  <TWLink
                    to="/sign-up"
                    variant="stylized"
                    className="flex justify-center typography-heading-sm my-[48px]"
                  >
                    Make an account to reply.
                  </TWLink>
                )}
              </>
            ) : (
              <div className="mt-[12px]" />
            )}
            {commentsCount > 0 ? (
              <>
                <Suspense
                  fallback={
                    <div className="flex flex-col gap-6 w-full" suppressHydrationWarning>
                      {(realRec.commentsCount || 0) !== 0 ? (
                        <>
                          {[...Array(Math.min(realRec.commentsCount!, 5))].map((_, index) => (
                            <SkeletonCommentItem key={index} />
                          ))}
                        </>
                      ) : null}
                    </div>
                  }
                >
                  <CommentsContainer
                    recId={realRec?.id || id}
                    estimatedCommentCount={realRec.commentsCount}
                  />
                </Suspense>
              </>
            ) : (
              <NoComments />
            )}
          </div>
        </div>
        {rec.user && (
          <div className={'py-4 flex-1 text-brand-main md:flex-none md:w-[40%] ' + 'max-w-full md:max-w-[40%]'}>
            <Suspense
              fallback={
                <>
                  <h2 className={'px-4 text-brand-main md:px-0 typography-action-sm md:text-xl font-semibold pb-6'}>
                    Related Recs
                  </h2>
                  <div className="flex flex-col gap-[12px] px-4 md:px-0 mb-3">
                    {[...Array(3)].map((_, index) => (
                      <SkeletonFeedItem key={index} skeletons={1} />
                    ))}
                  </div>
                  <div className={'mb-16 text-secondary-body-link'} />
                  <h2 className={'px-4 md:px-0 typography-action-sm md:text-xl font-semibold pb-6'}>
                    Top recs from @{rec.user?.username}
                  </h2>
                  <div className="flex flex-col gap-[12px] px-4 md:px-0 mb-3">
                    {[...Array(3)].map((_, index) => (
                      <SkeletonFeedItem key={index} skeletons={1} />
                    ))}
                  </div>
                </>
              }
            >
              <RecViewRelatedRecs
                recId={rec.id}
                userId={rec.user?.id}
                initialRelatedRecsData={initialRelatedRecsData}
              />
            </Suspense>
          </div>
        )}
      </div>
    </RecommendationContext.Provider>
  );
}
